/**
 * Motion Detection Library
 *
 * Coordinates motion detection using video frames:
 * 1. Capture frames from video element at configured interval
 * 2. Downsample to reduce processing load
 * 3. Send to Web Worker for frame differencing
 * 4. Track consecutive detections for reliable alerts
 */

export type MotionDetectionStatus =
  | "idle"
  | "initializing"
  | "running"
  | "paused"
  | "error";

export interface MotionDetectionConfig {
  // Processing interval in milliseconds
  processingIntervalMs: number;

  // Downsampled resolution (width) - height calculated from aspect ratio
  downsampleWidth: number;

  // Sensitivity threshold (0-255) - lower = more sensitive
  sensitivity: number;

  // Minimum percentage of pixels that must change to trigger motion
  motionThreshold: number;

  // Number of consecutive detections required to trigger alert
  consecutiveDetectionsRequired: number;

  // Cooldown period after alert in milliseconds
  cooldownAfterAlertMs: number;

  // Use region of interest (center-weighted)
  useRegionOfInterest: boolean;

  // Apply noise reduction
  noiseReduction: boolean;
}

export interface MotionDetectionResult {
  // Whether motion was detected (after consecutive check)
  motionDetected: boolean;

  // Motion level (0-100)
  motionLevel: number;

  // Percentage of pixels that changed
  motionPercentage: number;

  // Number of consecutive detections
  consecutiveDetections: number;

  // Detected hotspots (areas with most motion)
  hotspots: Array<{ x: number; y: number; intensity: number }>;

  // Timestamp
  timestamp: number;
}

export interface MotionDetectionCallbacks {
  // Called when motion is detected (after consecutive check)
  onMotionDetected?: (result: MotionDetectionResult) => void;

  // Called on each analysis cycle
  onAnalysis?: (result: MotionDetectionResult) => void;

  // Called when status changes
  onStatusChange?: (status: MotionDetectionStatus) => void;

  // Called on error
  onError?: (error: Error) => void;
}

// Default configuration - balanced for most devices
export const DEFAULT_MOTION_DETECTION_CONFIG: MotionDetectionConfig = {
  processingIntervalMs: 500, // Check every 500ms
  downsampleWidth: 160, // Low resolution for efficiency
  sensitivity: 25, // Moderate sensitivity
  motionThreshold: 2.5, // 2.5% of pixels must change
  consecutiveDetectionsRequired: 2, // Require 2 consecutive detections
  cooldownAfterAlertMs: 10000, // 10 second cooldown
  useRegionOfInterest: true,
  noiseReduction: true,
};

/**
 * Main Motion Detector class
 */
export class MotionDetector {
  private config: MotionDetectionConfig;
  private callbacks: MotionDetectionCallbacks;
  private worker: Worker | null = null;
  private videoElement: HTMLVideoElement | null = null;
  private canvas: OffscreenCanvas | null = null;
  private canvasContext: OffscreenCanvasRenderingContext2D | null = null;

  private status: MotionDetectionStatus = "idle";
  private processingInterval: ReturnType<typeof setInterval> | null = null;
  private consecutiveDetections = 0;
  private lastAlertTime = 0;
  private isInCooldown = false;
  private isProcessing = false;

  constructor(
    config: Partial<MotionDetectionConfig> = {},
    callbacks: MotionDetectionCallbacks = {}
  ) {
    this.config = { ...DEFAULT_MOTION_DETECTION_CONFIG, ...config };
    this.callbacks = callbacks;
  }

  /**
   * Initialize the motion detector with a video element
   */
  async initialize(videoElement: HTMLVideoElement): Promise<void> {
    this.setStatus("initializing");

    try {
      this.videoElement = videoElement;

      // Wait for video to have dimensions
      await this.waitForVideoDimensions();

      // Create offscreen canvas for frame capture
      const aspectRatio = videoElement.videoHeight / videoElement.videoWidth;
      const downsampleHeight = Math.round(
        this.config.downsampleWidth * aspectRatio
      );

      this.canvas = new OffscreenCanvas(
        this.config.downsampleWidth,
        downsampleHeight
      );
      this.canvasContext = this.canvas.getContext("2d", {
        willReadFrequently: true,
      });

      if (!this.canvasContext) {
        throw new Error("Failed to create canvas context");
      }

      // Initialize Web Worker
      await this.initializeWorker();

      this.setStatus("idle");
      console.log(
        "[MotionDetector] Initialized successfully",
        `(${this.config.downsampleWidth}x${downsampleHeight})`
      );
    } catch (error) {
      this.setStatus("error");
      const err = error instanceof Error ? error : new Error(String(error));
      this.callbacks.onError?.(err);
      throw error;
    }
  }

  /**
   * Wait for video element to have valid dimensions
   */
  private waitForVideoDimensions(): Promise<void> {
    return new Promise((resolve, reject) => {
      const video = this.videoElement;
      if (!video) {
        reject(new Error("No video element"));
        return;
      }

      // Check if already has dimensions
      if (video.videoWidth > 0 && video.videoHeight > 0) {
        resolve();
        return;
      }

      // Wait for loadedmetadata event
      const timeout = setTimeout(() => {
        reject(new Error("Timeout waiting for video dimensions"));
      }, 10000);

      const handleLoaded = () => {
        clearTimeout(timeout);
        video.removeEventListener("loadedmetadata", handleLoaded);
        resolve();
      };

      video.addEventListener("loadedmetadata", handleLoaded);
    });
  }

  /**
   * Initialize the Web Worker
   */
  private async initializeWorker(): Promise<void> {
    return new Promise((resolve, reject) => {
      try {
        this.worker = new Worker(
          new URL("../../workers/motionDetection.worker.ts", import.meta.url),
          { type: "module" }
        );

        const timeoutId = setTimeout(() => {
          reject(new Error("Worker initialization timeout"));
        }, 10000);

        this.worker.onmessage = (event) => {
          const { type, payload } = event.data;

          if (type === "initialized") {
            clearTimeout(timeoutId);
            console.log("[MotionDetector] Worker initialized");
            resolve();
          } else if (type === "error") {
            clearTimeout(timeoutId);
            reject(new Error(payload.message));
          } else if (type === "motion-result") {
            this.handleMotionResult(payload);
          }
        };

        this.worker.onerror = (error) => {
          clearTimeout(timeoutId);
          reject(error);
        };

        // Send initialization message
        this.worker.postMessage({
          type: "initialize",
          payload: {
            sensitivity: this.config.sensitivity,
            motionThreshold: this.config.motionThreshold,
            useRegionOfInterest: this.config.useRegionOfInterest,
            noiseReduction: this.config.noiseReduction,
          },
        });
      } catch (error) {
        reject(error);
      }
    });
  }

  /**
   * Start motion detection
   */
  start(): void {
    if (this.status === "running") return;

    if (!this.videoElement || !this.canvas || !this.canvasContext) {
      console.error("[MotionDetector] Cannot start: not initialized");
      return;
    }

    this.setStatus("running");
    this.consecutiveDetections = 0;
    this.isInCooldown = false;

    // Reset the worker's reference frame
    this.worker?.postMessage({ type: "reset", payload: {} });

    // Start processing loop
    this.processingInterval = setInterval(() => {
      this.processFrame();
    }, this.config.processingIntervalMs);

    console.log(
      "[MotionDetector] Started with interval:",
      this.config.processingIntervalMs,
      "ms"
    );
  }

  /**
   * Pause motion detection
   */
  pause(): void {
    if (this.status !== "running") return;

    if (this.processingInterval) {
      clearInterval(this.processingInterval);
      this.processingInterval = null;
    }

    this.setStatus("paused");
    console.log("[MotionDetector] Paused");
  }

  /**
   * Resume motion detection
   */
  resume(): void {
    if (this.status !== "paused") return;
    this.start();
  }

  /**
   * Stop and cleanup
   */
  stop(): void {
    if (this.processingInterval) {
      clearInterval(this.processingInterval);
      this.processingInterval = null;
    }

    if (this.worker) {
      this.worker.terminate();
      this.worker = null;
    }

    this.canvas = null;
    this.canvasContext = null;
    this.videoElement = null;
    this.consecutiveDetections = 0;
    this.setStatus("idle");
    console.log("[MotionDetector] Stopped");
  }

  /**
   * Process a single frame
   */
  private processFrame(): void {
    // Skip if already processing or in cooldown
    if (this.isProcessing) return;

    // Check cooldown
    if (this.isInCooldown) {
      const timeSinceAlert = Date.now() - this.lastAlertTime;
      if (timeSinceAlert >= this.config.cooldownAfterAlertMs) {
        this.isInCooldown = false;
        console.log("[MotionDetector] Cooldown ended");
      } else {
        return;
      }
    }

    const video = this.videoElement;
    const canvas = this.canvas;
    const ctx = this.canvasContext;

    if (!video || !canvas || !ctx || !this.worker) return;

    // Check if video is actually playing
    if (video.paused || video.ended || video.readyState < 2) return;

    this.isProcessing = true;

    try {
      // Draw video frame to canvas (downsampled)
      ctx.drawImage(video, 0, 0, canvas.width, canvas.height);

      // Get image data
      const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);

      // Send to worker for processing
      this.worker.postMessage({
        type: "process-frame",
        payload: {
          imageData: imageData.data,
          width: canvas.width,
          height: canvas.height,
          timestamp: Date.now(),
        },
      });
    } catch (error) {
      console.error("[MotionDetector] Frame processing error:", error);
    } finally {
      this.isProcessing = false;
    }
  }

  /**
   * Handle motion result from worker
   */
  private handleMotionResult(payload: {
    motionDetected: boolean;
    motionLevel: number;
    motionPercentage: number;
    changedPixels: number;
    totalPixels: number;
    timestamp: number;
    isFirstFrame: boolean;
    hotspots?: Array<{ x: number; y: number; intensity: number }>;
  }): void {
    // Skip first frame
    if (payload.isFirstFrame) return;

    // Update consecutive detections counter
    if (payload.motionDetected) {
      this.consecutiveDetections++;
    } else {
      this.consecutiveDetections = Math.max(0, this.consecutiveDetections - 1);
    }

    const motionConfirmed =
      this.consecutiveDetections >= this.config.consecutiveDetectionsRequired;

    const result: MotionDetectionResult = {
      motionDetected: motionConfirmed,
      motionLevel: payload.motionLevel,
      motionPercentage: payload.motionPercentage,
      consecutiveDetections: this.consecutiveDetections,
      hotspots: payload.hotspots || [],
      timestamp: payload.timestamp,
    };

    this.callbacks.onAnalysis?.(result);

    // Trigger alert if motion is confirmed and not in cooldown
    if (motionConfirmed && !this.isInCooldown) {
      this.triggerMotionAlert(result);
    }
  }

  /**
   * Trigger motion alert
   */
  private triggerMotionAlert(result: MotionDetectionResult): void {
    this.lastAlertTime = Date.now();
    this.isInCooldown = true;
    this.consecutiveDetections = 0;

    console.log("[MotionDetector] MOTION DETECTED!", result);
    this.callbacks.onMotionDetected?.(result);
  }

  /**
   * Set status and notify callback
   */
  private setStatus(status: MotionDetectionStatus): void {
    this.status = status;
    this.callbacks.onStatusChange?.(status);
  }

  /**
   * Get current status
   */
  getStatus(): MotionDetectionStatus {
    return this.status;
  }

  /**
   * Update configuration
   */
  updateConfig(config: Partial<MotionDetectionConfig>): void {
    const wasRunning = this.status === "running";

    if (wasRunning) {
      this.pause();
    }

    this.config = { ...this.config, ...config };

    // Update worker config
    if (this.worker) {
      this.worker.postMessage({
        type: "update-config",
        payload: {
          sensitivity: this.config.sensitivity,
          motionThreshold: this.config.motionThreshold,
          useRegionOfInterest: this.config.useRegionOfInterest,
          noiseReduction: this.config.noiseReduction,
        },
      });
    }

    if (wasRunning) {
      this.start();
    }
  }

  /**
   * Get current configuration
   */
  getConfig(): MotionDetectionConfig {
    return { ...this.config };
  }
}
